import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ArrowLeft } from "react-feather";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ImageWithBasePath from "core/img/imagewithbasebath";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import { all_routes } from "Router/all_routes";
import * as Yup from "yup";
import { Table } from "antd";
import { toast } from "core/Operations/toast";
import { useImportEmployeesMutation } from "AppQuries/employee/import-employee";
import {
  getCurrentFormattedDate,
  parseExcelDateToISO,
} from "core/Operations/dateFormat";
import { sampleAllowanceAndDeductionData } from "core/json/sample_data";
import { importEmpAllowanceDeductionValidation } from "core/Operations/ValidationSchema/payrollValidation";
import { EmpAllowanceAndDeductionsColumnMap } from "./import/AllowanceDeductionColumnMapping";
import { useImportEmpAllowanceDeductionMutation } from "AppQuries/payroll/emp-allowance-deduction";
import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
import { useGetAllDepartmentQuery } from "AppQuries/master/Department/get-all-departments";
import { BackIcon } from "Components/UI/Icon/Icons";

const ImportEmpAllowanceAndDeduction = () => {
  const userData = useSelector(loggedInUser);
  const companyCode = userData?.companyCode;
  const [importData, setImportData] = useState([]);
  const navigate = useNavigate();
  const validationSchema = Yup.array().of(
    importEmpAllowanceDeductionValidation
  );

  const { data: employees } = useGetEmployeeQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    departmentCode: 0,
  });

  const { data: departments } = useGetAllDepartmentQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    isTrue: true,
  });
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");

  const getEmployeeCode = (uniqueID) => {
    const employee = employees?.find((emp) => emp.uniqueId === uniqueID);
    return employee ? employee.code : 0;
  };

  const getDepartmentCode = (departmentName) => {
    const department = departments?.data?.find(
      (d) => d.description.toLowerCase() === departmentName.toLowerCase()
    );
    return department ? department.code.toString() : "";
  };

  const flattenJson = (data) => {
    const result = {};
    const recurse = (cur, prop) => {
      if (Object(cur) !== cur || Array.isArray(cur)) {
        result[prop] = Array.isArray(cur) ? JSON.stringify(cur) : cur;
      } else {
        for (const key in cur) {
          recurse(cur[key], prop ? `${prop}_${key}` : key);
        }
      }
    };
    recurse(data, "");
    return result;
  };

  const handleDownloadSample = () => {
    const flatData = flattenJson(sampleAllowanceAndDeductionData);
    const headers = Object.keys(flatData);
    const sample = [headers.map((key) => flatData[key])];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sample]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Sample_Addition_DEDUCTION.xlsx");
  };

  const { mutate: importAllowanceAndDeduction } =
    useImportEmpAllowanceDeductionMutation();

  const formatExcelTime = (excelTime) => {
    const totalMinutes = Math.round(excelTime * 24 * 60);
    const hours = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (totalMinutes % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      setUploadStatus("Processing...");
      setUploadedFileName(file.name);
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      // Convert headers to internal keys
      const mappedData = jsonData.map((row) => {
        const mappedRow = {};
        for (let header in row) {
          const key = EmpAllowanceAndDeductionsColumnMap[header];
          if (key) {
            mappedRow[key] = row[header];
          }
        }

        return mappedRow;
      });


      const finalData = mappedData?.map((updatedRow, index) => {
        return {
          companyCode: userData?.companyCode,
          adjType: updatedRow?.adjType === "Addition" ? 1 : 0,
          adjName: updatedRow?.adjName,
          employeeCode: 0, //  getEmployeeCode(updatedRow?.employeeCode), //updatedRow?.employeeCode,
          uniqueId: updatedRow?.employeeCode,
          amount: Number(updatedRow?.amount),
          departmentCode: updatedRow?.departmentCode, //getDepartmentCode(updatedRow?.departmentCode), //updatedRow?.departmentCode,
          locationCode: userData?.locationCode,
          createUser: userData?.userName,
          isActive: true,
          isTemporary: true,
          effectiveDateFrom: parseExcelDateToISO(updatedRow?.effectiveDateFrom),
          effectiveDateTo: parseExcelDateToISO(updatedRow?.effectiveDateFrom),
          employeeName: updatedRow?.employeeName,
          departmentName: updatedRow?.departmentCode,
        };
      });

      console.log("finalData", finalData);

      validationSchema
        .validate(finalData)
        .then(() => {
          setUploadStatus("File processed successfully!");
          setImportData(finalData);
        })
        .catch((err) => {
          console.error("Validation error:", err);
          setUploadStatus("Validation error: " + err.message);
        });
    };

    reader.readAsArrayBuffer(file);
  };

  // Function to remove the uploaded file
  const handleRemoveFile = () => {
    setUploadedFileName("");
    setImportData([]);
    setUploadStatus("");
  };

  const importEmployeeData = () => {
    importAllowanceAndDeduction(importData, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success(data[0].errorMessage);
          navigate(
            `${all_routes.emp_allowance_deduction}`
          );
        } else {
          toast.warning(data[0].errorMessage);
        }
      },

      onError: (error) => {
        toast.warning(error.message || "Something went wrong.");
      },
    });
    setUploadedFileName("");
    setImportData([]);
    setUploadStatus("");
  };

  const columns = [
    {
      title: "Employee Code",
      dataIndex: "uniqueId",
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
    },
    {
      title: "Adjustment Type",
      dataIndex: "adjType",
      render: (text) => (text === 1 ? "Addition" : "Deduction"),
    },
    {
      title: "Adjustment Name",
      dataIndex: "adjName",
    },
    {
      title: "Amount",
      dataIndex: "amount",
    },
   
    {
      title: "From Date",
      dataIndex: "effectiveDateFrom",
    },
    {
      title: "To Date",
      dataIndex: "effectiveDateTo",
    },

    {
      title: "Department",
      dataIndex: "departmentName",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Import Employee Allowance And Deduction</h4>
              <h6>Add Employees Allowance And Deduction from excel sheet</h6>
            </div>
          </div>

          <div className="page-btn">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="add-new-tooltip">Back</Tooltip>}
            >
              <Link
                to={`${all_routes.emp_allowance_deduction}`}
                className="btn btn-secondary"
              >
                <BackIcon className="me-2" />
                Back
              </Link>
            </OverlayTrigger>
          </div>
        </div>
        <div className="card border-0">
          <div className="card-body">
            <div className="row form-row">
              <div className="col-lg-12">
                <div className="modal-footer-btn download-file mb-3">
                  <button
                    onClick={handleDownloadSample}
                    // onClick={() => generateExcel({ optionData })}
                    className="btn btn-submit"
                    type="button"
                  >
                    Download Sample File
                  </button>
                  &nbsp;
                  <button
                    // onClick={handleDownloadSample}
                    onClick={importEmployeeData}
                    className="btn btn-submit"
                    type="button"
                    disabled={!uploadedFileName || importData.length === 0}
                  >
                    Import
                  </button>
                </div>
              </div>

              <>
                <div className="col-lg-12">
                  <div className="input-blocks image-upload-down mb-10">
                    <label> Upload Excel File</label>
                    <div className="image-upload download">
                      {!uploadedFileName ? (
                        <>
                          <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileUpload}
                          />
                          <div className="image-uploads">
                            <ImageWithBasePath
                              src="assets/img/download-img.png"
                              alt="img"
                            />
                            <h4>
                              Drag and drop a <span>file to upload</span>
                            </h4>
                          </div>
                        </>
                      ) : (
                        <div className="uploaded-file-info">
                          <div className="image-uploads">
                            <p>Uploaded File: {uploadedFileName}</p>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={handleRemoveFile}
                            >
                              Remove File
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-lg-12" style={{ marginTop: "75px" }}>
                  {uploadStatus && (
                    <div
                      className={`alert ${
                        uploadStatus.includes("Successfully")
                          ? "alert-success"
                          : "alert-info"
                      } mt-3`}
                    >
                      {uploadStatus}
                    </div>
                  )}
                </div>
                <div className="col-lg-12">
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={importData}
                      pagination={false}
                      rowKey={(record) => record.uniqueId}
                    />
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportEmpAllowanceAndDeduction;
